import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MockBadge } from "@/components/common/MockBadge";
import { cn } from "@/lib/cn";
import type { StatusTone } from "@/types/domain";

const TONE_BAR: Record<StatusTone, string> = {
  info: "bg-blue-500",
  attention: "bg-amber-500",
  critical: "bg-red-500",
  success: "bg-emerald-500",
  neutral: "bg-slate-400",
};

interface KpiCardProps {
  label: string;
  value: string | number;
  tone: StatusTone;
  /** Navigation interne optionnelle. Absente = carte non cliquable. */
  to?: string;
  mockLabel?: string;
  compact?: boolean;
  testId: string;
}

export const KpiCard = ({
  label,
  value,
  tone,
  to,
  mockLabel,
  compact = false,
  testId,
}: KpiCardProps) => {
  const content = (
    <>
      <span className={cn("absolute inset-x-0 top-0 h-0.5", TONE_BAR[tone])} />
      <div className="flex items-start justify-between gap-2">
        <p className="ops-label leading-snug">{label}</p>
        {mockLabel && <MockBadge label={mockLabel} />}
      </div>
      <p
        className={cn(
          "mt-3 font-mono font-semibold tabular-nums",
          compact ? "text-2xl" : "text-3xl",
        )}
      >
        {value}
      </p>
      {to && (
        <ArrowUpRight className="absolute bottom-3 right-3 h-4 w-4 text-muted-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
      )}
    </>
  );

  const base =
    "ops-panel group relative block overflow-hidden p-4 transition-colors duration-150 hover:border-ring/60";

  if (!to) {
    return (
      <article data-testid={testId} className={base}>
        {content}
      </article>
    );
  }

  return (
    <Link to={to} data-testid={testId} className={cn(base, "ops-focus cursor-pointer")}>
      {content}
    </Link>
  );
};
